import {
  collection,
  DocumentData,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  QueryConstraint,
  QuerySnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, firestore, functions } from '../firebase';
import {
  AppNotification,
  AppNotificationEntityType,
  AppNotificationType,
} from '../types/notifications';
import { repairRequestService } from './repairRequestService';
import { RepairRequestUnreadSection } from '../utils/repairRequestReadModel';

interface SubscribeNotificationOptions {
  onlyUnread?: boolean;
  maxResults?: number;
}

interface MarkEntityNotificationsAsReadParams {
  recipientUserId: string;
  entityType: AppNotificationEntityType | string;
  entityId: string;
}

interface MarkRepairRequestNotificationsAsReadParams {
  recipientUserId: string;
  repairRequestId: string;
}

interface MarkCommunityNotificationAsReadParams {
  notificationId: string;
  threadId: string;
  postId?: string | null;
}

interface MarkVerificationNotificationAsReadParams {
  notificationId: string;
  providerUserId: string;
}

interface MarkServiceVerificationDecisionNotificationAsReadParams {
  notificationId: string;
  providerUserId: string;
  decisionId: string;
  outcome: 'approved' | 'changes_requested';
}

export interface ServiceVerificationDecisionOutcome {
  decisionId: string;
  outcome: 'approved' | 'changes_requested';
  occurredAtMillis: number;
}

interface MarkCommunityReportOccurrenceNotificationAsReadParams {
  notificationId: string;
  reportId: string;
  occurrenceId: string;
  occurrenceVersion: number;
}

interface MarkCommunityOutcomeNotificationAsReadParams {
  notificationId: string;
  resolutionStatus: 'reviewed' | 'dismissed' | 'action_taken';
}

interface MarkCommunityModerationOutcomeNotificationAsReadParams {
  notificationId: string;
  moderationAction:
    | 'post_hidden'
    | 'thread_hidden'
    | 'thread_restored'
    | 'thread_closed'
    | 'thread_reopened';
}

interface MarkLegacyCommunityReportNotificationAsReadParams {
  notificationId: string;
  reportId: string;
  occurrenceId?: never;
  occurrenceVersion?: never;
}

type MarkCommunityReportNotificationAsReadParams =
  | MarkCommunityReportOccurrenceNotificationAsReadParams
  | MarkLegacyCommunityReportNotificationAsReadParams;

interface MarkSpecializationNotificationAsReadParams {
  notificationId: string;
  specializationId: string;
}

interface MarkSpecializationOutcomeNotificationAsReadParams {
  notificationId: string;
  specializationId: string;
  outcome: 'accepted' | 'rejected';
}

interface MarkEquipmentTypeSuggestionNotificationAsReadParams {
  notificationId: string;
  suggestionId: string;
}

interface MarkEquipmentTypeSuggestionOutcomeNotificationAsReadParams {
  notificationId: string;
  suggestionId: string;
  outcome: 'accepted' | 'rejected';
}

interface MarkProductFeedbackNotificationAsReadParams {
  notificationId: string;
  reportId: string;
}

interface MarkProductFeedbackReporterNotificationAsReadParams {
  notificationId: string;
  reportId: string;
}

interface MarkSuggestionNotificationAsReadParams {
  notificationId: string;
  suggestionId: string;
  suggestionView: 'original' | 'technical';
  host: 'user' | 'admin';
}

interface ResolveNotificationTargetOptions {
  canAccessServiceProfile: boolean;
  defaultTarget?: string;
}

export type BellCommand =
  | {
      source: 'notifications';
      action: 'markRead' | 'dismiss';
      id: string;
      entityType?: string;
      entityId?: string;
    }
  | {
      source: 'repairRequests';
      action: 'markRead';
      requestId: string;
      unreadSection: RepairRequestUnreadSection;
      actor: 'client' | 'service';
    };

export interface BellCommandResult {
  command: BellCommand;
  ok: boolean;
  error?: Error;
}

interface NotificationNavigationTarget {
  target: string;
  primaryTarget: string;
  fallbackTarget: string | null;
  usesFallback: boolean;
}

const COMMUNITY_MODERATION_OUTCOME_ACTIONS = new Set([
  'post_hidden',
  'thread_hidden',
  'thread_restored',
  'thread_closed',
  'thread_reopened',
]);

const ENTITY_MARK_READ_CHUNK_SIZE = 450;
export const REPAIR_REQUEST_ENTITY_TYPES: ReadonlyArray<string> = [
  'repair_request',
  'repairRequest',
  'repair-request',
];

export const isRepairRequestNotification = (
  notification: Pick<AppNotification, 'entityType'> | null | undefined
): boolean => {
  const entityType = String(notification?.entityType || '').trim();
  return REPAIR_REQUEST_ENTITY_TYPES.includes(entityType);
};

const SUGGESTION_NOTIFICATION_TYPES: ReadonlySet<AppNotificationType> = new Set([
  'admin_new_suggestion',
  'suggestion_admin_reply',
  'suggestion_accepted',
  'suggestion_rejected',
  'suggestion_voting_opened',
  'suggestion_voting_reminder',
  'suggestion_voting_closed_accepted',
  'suggestion_voting_closed_rejected',
  'suggestion_declined_by_admin',
  'suggestion_implementation_planned',
  'suggestion_implementation_in_progress',
  'suggestion_implementation_implemented',
] as AppNotificationType[]);

const SPECIALIZATION_DECISION_NOTIFICATION_TYPES: ReadonlySet<AppNotificationType> = new Set([
  'specialization_suggestion_accepted',
  'specialization_suggestion_rejected',
] as AppNotificationType[]);

const EQUIPMENT_TYPE_SUGGESTION_NOTIFICATION_TYPES: ReadonlySet<AppNotificationType> = new Set([
  'admin_new_equipment_type_suggestion',
] as AppNotificationType[]);

const EQUIPMENT_TYPE_SUGGESTION_OUTCOME_NOTIFICATION_TYPES: ReadonlySet<AppNotificationType> =
  new Set([
    'equipment_type_suggestion_accepted',
    'equipment_type_suggestion_rejected',
  ] as AppNotificationType[]);

const SERVICE_VERIFICATION_DECISION_NOTIFICATION_TYPES: ReadonlySet<AppNotificationType> = new Set([
  'service_verification_approved',
  'service_verification_changes_requested',
] as AppNotificationType[]);

const COMMUNITY_NOTIFICATION_TYPES: ReadonlySet<AppNotificationType> = new Set([
  'community_new_thread',
  'community_reply',
  'community_thread_reply',
  'community_quote',
  'community_gratitude_targeted',
  'community_gratitude_reply',
  'community_announcement',
  'community_moderation_action',
] as AppNotificationType[]);

const normalizeTarget = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (!trimmed.startsWith('/')) {
    return null;
  }

  return trimmed;
};

const getSuggestionTarget = (notification: AppNotification, entityId: string): string => {
  const params = new URLSearchParams();
  params.set('suggestionId', entityId);
  params.set(
    'suggestionView',
    notification.type === 'admin_new_suggestion' ||
      notification.type === 'suggestion_admin_reply' ||
      notification.type === 'suggestion_accepted' ||
      notification.type === 'suggestion_rejected' ||
      notification.type === 'suggestion_declined_by_admin'
      ? 'original'
      : 'technical'
  );
  if (notification.id) {
    params.set('notificationId', notification.id);
  }
  return `${notification.type === 'admin_new_suggestion' ? '/admin' : '/app'}/suggestions?${params.toString()}`;
};

const getSpecializationPrimaryTarget = (
  notification: AppNotification,
  entityId: string
): string => {
  const params = new URLSearchParams();
  params.set('specializationSuggestionId', entityId);
  const notificationId = String(notification.id || '').trim();
  if (notificationId) {
    params.set('notificationId', notificationId);
  }
  return `/app/service-profile?${params.toString()}`;
};

const getCommunityTarget = (notification: AppNotification, entityId: string): string => {
  const threadId = String(
    (notification as AppNotification & { threadId?: string }).threadId || entityId
  ).trim();
  if (!threadId) return '/app/community';

  const postId = String(
    (notification as AppNotification & { postId?: string }).postId || ''
  ).trim();
  const params = new URLSearchParams();
  if (postId) params.set('postId', postId);
  if (notification.id) params.set('notificationId', notification.id);
  const queryString = params.toString();
  return `/app/community/thread/${threadId}${queryString ? `?${queryString}` : ''}`;
};

const getVerificationTarget = (notification: AppNotification, entityId: string): string => {
  if (!entityId) return '/admin/verifications';

  const params = new URLSearchParams();
  params.set('userId', entityId);
  const notificationId = String(notification.id || '').trim();
  if (notificationId) {
    params.set('notificationId', notificationId);
  }
  return `/admin/verifications?${params.toString()}`;
};

const getServiceVerificationDecisionTarget = (
  notification: AppNotification,
  entityId: string
): string => {
  const decisionId = String(notification.verificationDecisionId || '').trim();
  if (!entityId || !isSafeDocumentId(entityId) || !isSafeDocumentId(decisionId)) {
    return '/app/service-profile';
  }
  const params = new URLSearchParams();
  params.set('verificationDecisionId', decisionId);
  if (notification.id) params.set('notificationId', notification.id);
  return `/app/service-profile?${params.toString()}`;
};

const getCommunityReportTarget = (notification: AppNotification, entityId: string): string => {
  if (!entityId) return '/admin/community';

  const params = new URLSearchParams();
  params.set('reportId', entityId);
  const occurrenceId = String(notification.occurrenceId || '').trim();
  const occurrenceVersionRaw = String(notification.occurrenceVersion ?? '').trim();
  if (occurrenceId) {
    params.set('occurrenceId', occurrenceId);
  }
  if (occurrenceVersionRaw) {
    params.set('occurrenceVersion', occurrenceVersionRaw);
  }
  const notificationId = String(notification.id || '').trim();
  if (notificationId) {
    params.set('notificationId', notificationId);
  }
  return `/admin/community?${params.toString()}`;
};

const getCommunityReportOutcomeTarget = (notification: AppNotification): string => {
  const notificationId = String(notification.id || '').trim();
  if (!isSafeDocumentId(notificationId)) return '/app/community';
  const params = new URLSearchParams({ notificationId });
  return `/app/community/outcomes?${params.toString()}`;
};

const getCommunityModerationOutcomeTarget = (notification: AppNotification): string => {
  const action = String(notification.moderationAction || '').trim();
  const notificationId = String(notification.id || '').trim();
  if (!COMMUNITY_MODERATION_OUTCOME_ACTIONS.has(action) || !isSafeDocumentId(notificationId)) {
    return '/app/community';
  }
  return `/app/community/outcomes?notificationId=${encodeURIComponent(notificationId)}`;
};

const getProductFeedbackTarget = (notification: AppNotification, entityId: string): string => {
  if (!entityId) return '/admin/product-feedback';

  const params = new URLSearchParams();
  params.set('reportId', entityId);
  const notificationId = String(notification.id || '').trim();
  if (notificationId) {
    params.set('notificationId', notificationId);
  }
  return `/admin/product-feedback?${params.toString()}`;
};

const getProductFeedbackReporterTarget = (
  notification: AppNotification,
  entityId: string
): string => {
  if (!entityId) return '/app/product-feedback';

  const params = new URLSearchParams();
  params.set('reportId', entityId);
  const notificationId = String(notification.id || '').trim();
  if (notificationId) {
    params.set('notificationId', notificationId);
  }
  return `/app/product-feedback?${params.toString()}`;
};

const getSpecializationSuggestionTarget = (
  notification: AppNotification,
  entityId: string
): string => {
  const params = new URLSearchParams();
  params.set('view', 'moderation');
  if (!entityId) {
    return `/admin/specializations?${params.toString()}`;
  }

  params.set('specializationId', entityId);
  const notificationId = String(notification.id || '').trim();
  if (notificationId) {
    params.set('notificationId', notificationId);
  }
  return `/admin/specializations?${params.toString()}`;
};

const getEquipmentTypeSuggestionTarget = (
  notification: AppNotification,
  entityId: string
): string => {
  const params = new URLSearchParams({
    view: 'moderation',
  });
  if (!entityId) {
    return '/admin/equipment-types?view=moderation';
  }
  params.set('equipmentTypeSuggestionId', entityId);
  const notificationId = String(notification.id || '').trim();
  if (notificationId) {
    params.set('notificationId', notificationId);
  }
  return `/admin/equipment-types?${params.toString()}`;
};

const getEquipmentTypeSuggestionOutcomeTarget = (
  notification: AppNotification,
  entityId: string
): string => {
  if (!entityId) {
    return '/app/service/equipment-types';
  }

  const params = new URLSearchParams();
  params.set('equipmentTypeSuggestionId', entityId);
  const notificationId = String(notification.id || '').trim();
  if (notificationId) {
    params.set('notificationId', notificationId);
  }
  return `/app/service/equipment-types?${params.toString()}`;
};

const mapSnapshotToNotifications = (snapshot: QuerySnapshot<DocumentData>): AppNotification[] => {
  const notifications = snapshot.docs.map((docSnapshot) => ({
    id: docSnapshot.id,
    ...docSnapshot.data(),
  })) as AppNotification[];

  notifications.sort((a, b) => {
    const left = a.createdAt?.toMillis?.() || 0;
    const right = b.createdAt?.toMillis?.() || 0;
    return right - left;
  });

  return notifications;
};

const subscribeToUserNotifications = (
  userId: string,
  onUpdate: (notifications: AppNotification[]) => void,
  onError: (error: Error) => void,
  options: SubscribeNotificationOptions = {}
): (() => void) => {
  const { onlyUnread = false, maxResults = 40 } = options;

  const baseCollection = collection(firestore, 'notifications');
  const constraints: QueryConstraint[] = [
    where('recipientUserId', '==', userId),
    where('dismissedAt', '==', null),
  ];

  if (onlyUnread) {
    constraints.push(where('status', '==', 'unread'));
  }

  constraints.push(orderBy('createdAt', 'desc'));
  constraints.push(limit(Math.max(1, Math.min(100, maxResults))));

  const notificationsQuery = query(baseCollection, ...constraints);

  return onSnapshot(
    notificationsQuery,
    (snapshot) => {
      onUpdate(mapSnapshotToNotifications(snapshot));
    },
    (error) => {
      onError(error instanceof Error ? error : new Error(String(error)));
    }
  );
};

const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  if (!notificationId) {
    return;
  }

  const notificationRef = doc(firestore, 'notifications', notificationId);
  await updateDoc(notificationRef, {
    status: 'read',
    readAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

const markSuggestionNotificationAsRead = async (
  params: MarkSuggestionNotificationAsReadParams
): Promise<boolean> => {
  const notificationId = String(params.notificationId || '').trim();
  const suggestionId = String(params.suggestionId || '').trim();
  const currentUserId = String(auth.currentUser?.uid || '').trim();
  if (!currentUserId || !isSafeDocumentId(notificationId) || !isSafeDocumentId(suggestionId, 256)) {
    return false;
  }

  const notificationRef = doc(firestore, 'notifications', notificationId);
  const snapshot = await getDoc(notificationRef);
  if (!snapshot.exists()) {
    return false;
  }

  const data = snapshot.data() || {};
  const expectedAdmin = params.host === 'admin';
  const allowedType = expectedAdmin
    ? data.type === 'admin_new_suggestion'
    : SUGGESTION_NOTIFICATION_TYPES.has(data.type as AppNotificationType) &&
      data.type !== 'admin_new_suggestion';
  const expectedPath = expectedAdmin ? '/admin/suggestions' : '/app/suggestions';
  const storedLink = String(data.deepLink || '').trim();
  let storedView = '';
  let storedPath = '';
  try {
    const parsed = new URL(storedLink, 'https://ratujsprzet.pl');
    storedPath = parsed.pathname;
    storedView = parsed.searchParams.get('suggestionView') || '';
  } catch (_error) {
    return false;
  }

  if (
    String(data.recipientUserId || '').trim() !== currentUserId ||
    data.entityType !== 'suggestion' ||
    String(data.entityId || '').trim() !== suggestionId ||
    !allowedType ||
    storedPath !== expectedPath ||
    storedView !== params.suggestionView
  ) {
    return false;
  }

  if (data.status === 'read' || Boolean(data.dismissedAt)) {
    return true;
  }
  if (data.status !== 'unread') {
    return false;
  }

  await markNotificationAsRead(notificationId);
  return true;
};

export const isSafeDocumentId = (value: string, maxLength = 256): boolean =>
  value.length > 0 && value.length <= maxLength && /^[A-Za-z0-9_-]+$/.test(value);

const markCommunityNotificationAsRead = async (
  params: MarkCommunityNotificationAsReadParams
): Promise<boolean> => {
  const notificationId = String(params.notificationId || '').trim();
  const threadId = String(params.threadId || '').trim();
  const postId = String(params.postId || '').trim();
  if (!isSafeDocumentId(notificationId) || !isSafeDocumentId(threadId, 160)) {
    return false;
  }
  if (postId && !isSafeDocumentId(postId, 160)) {
    return false;
  }

  const notificationRef = doc(firestore, 'notifications', notificationId);
  const snapshot = await getDoc(notificationRef);
  if (!snapshot.exists()) {
    return false;
  }
  const data = snapshot.data() || {};
  const type = data.type as AppNotificationType;
  const storedThreadId = String(data.threadId || '').trim();
  const storedPostId = String(data.postId || '').trim();
  if (
    !COMMUNITY_NOTIFICATION_TYPES.has(type) ||
    storedThreadId !== threadId ||
    storedPostId !== postId
  ) {
    return false;
  }
  if (data.status === 'read' || Boolean(data.dismissedAt)) {
    return true;
  }
  if (data.status !== 'unread') {
    return false;
  }

  await updateDoc(notificationRef, {
    status: 'read',
    readAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return true;
};

const markVerificationNotificationAsRead = async (
  params: MarkVerificationNotificationAsReadParams
): Promise<boolean> => {
  const notificationId = String(params.notificationId || '').trim();
  const providerUserId = String(params.providerUserId || '').trim();
  const currentUserId = String(auth.currentUser?.uid || '').trim();
  if (
    !currentUserId ||
    !isSafeDocumentId(notificationId) ||
    !providerUserId ||
    providerUserId.length > 128
  ) {
    return false;
  }

  const notificationRef = doc(firestore, 'notifications', notificationId);
  const snapshot = await getDoc(notificationRef);
  if (!snapshot.exists()) {
    return false;
  }

  const data = snapshot.data() || {};
  if (
    String(data.recipientUserId || '').trim() !== currentUserId ||
    data.type !== 'admin_new_verification' ||
    data.entityType !== 'verification' ||
    String(data.entityId || '').trim() !== providerUserId
  ) {
    return false;
  }

  if (data.status === 'read' || Boolean(data.dismissedAt)) {
    return true;
  }
  if (data.status !== 'unread') {
    return false;
  }

  await markNotificationAsRead(notificationId);
  return true;
};

const getOwnServiceVerificationDecisionOutcome = async (params: {
  notificationId: string;
  decisionId: string;
}): Promise<ServiceVerificationDecisionOutcome> => {
  const notificationId = String(params.notificationId || '').trim();
  const decisionId = String(params.decisionId || '').trim();
  if (!isSafeDocumentId(notificationId) || !isSafeDocumentId(decisionId)) {
    throw new Error('Nieprawidłowy identyfikator decyzji weryfikacyjnej.');
  }

  const callable = httpsCallable<
    { notificationId: string; decisionId: string },
    ServiceVerificationDecisionOutcome
  >(functions, 'getOwnServiceVerificationDecisionOutcome');
  const result = await callable({ notificationId, decisionId });
  const data = (result.data || {}) as Partial<ServiceVerificationDecisionOutcome>;
  if (
    String(data.decisionId || '').trim() !== decisionId ||
    (data.outcome !== 'approved' && data.outcome !== 'changes_requested') ||
    typeof data.occurredAtMillis !== 'number' ||
    !Number.isSafeInteger(data.occurredAtMillis) ||
    data.occurredAtMillis <= 0
  ) {
    throw new Error('Nieprawidłowa odpowiedź decyzji weryfikacyjnej.');
  }
  return {
    decisionId,
    outcome: data.outcome,
    occurredAtMillis: data.occurredAtMillis,
  };
};

const markServiceVerificationDecisionNotificationAsRead = async (
  params: MarkServiceVerificationDecisionNotificationAsReadParams
): Promise<boolean> => {
  const notificationId = String(params.notificationId || '').trim();
  const providerUserId = String(params.providerUserId || '').trim();
  const decisionId = String(params.decisionId || '').trim();
  const currentUserId = String(auth.currentUser?.uid || '').trim();
  const expectedType =
    params.outcome === 'approved'
      ? 'service_verification_approved'
      : params.outcome === 'changes_requested'
        ? 'service_verification_changes_requested'
        : null;
  if (
    !currentUserId ||
    currentUserId !== providerUserId ||
    !expectedType ||
    !isSafeDocumentId(notificationId) ||
    !isSafeDocumentId(decisionId) ||
    !isSafeDocumentId(providerUserId)
  ) {
    return false;
  }

  const notificationRef = doc(firestore, 'notifications', notificationId);
  const snapshot = await getDoc(notificationRef);
  if (!snapshot.exists()) return false;
  const data = snapshot.data() || {};
  if (
    String(data.recipientUserId || '').trim() !== currentUserId ||
    data.recipientRole !== 'user' ||
    data.type !== expectedType ||
    data.entityType !== 'verification' ||
    String(data.entityId || '').trim() !== providerUserId ||
    String(data.verificationDecisionId || '').trim() !== decisionId
  ) {
    return false;
  }
  if (data.status === 'read' || Boolean(data.dismissedAt)) return true;
  if (data.status !== 'unread') return false;
  await markNotificationAsRead(notificationId);
  return true;
};

const markCommunityReportNotificationAsRead = async (
  params: MarkCommunityReportNotificationAsReadParams
): Promise<boolean> => {
  const notificationId = String(params.notificationId || '').trim();
  const reportId = String(params.reportId || '').trim();
  const occurrenceId = String(params.occurrenceId || '').trim();
  const occurrenceVersion = Number(params.occurrenceVersion);
  const currentUserId = String(auth.currentUser?.uid || '').trim();
  if (
    !currentUserId ||
    !isSafeDocumentId(notificationId) ||
    !isSafeDocumentId(reportId, 256) ||
    (occurrenceId &&
      (!isSafeDocumentId(occurrenceId, 256) ||
        !Number.isInteger(occurrenceVersion) ||
        occurrenceVersion < 1))
  ) {
    return false;
  }

  const notificationRef = doc(firestore, 'notifications', notificationId);
  const snapshot = await getDoc(notificationRef);
  if (!snapshot.exists()) {
    return false;
  }

  const data = snapshot.data() || {};
  if (
    String(data.recipientUserId || '').trim() !== currentUserId ||
    data.type !== 'admin_new_community_report' ||
    data.entityType !== 'communityReport' ||
    String(data.entityId || '').trim() !== reportId ||
    (occurrenceId &&
      (String(data.occurrenceId || '').trim() !== occurrenceId ||
        Number(data.occurrenceVersion) !== occurrenceVersion))
  ) {
    return false;
  }

  const eventOccurrenceId = String(data.occurrenceId || '').trim();
  const eventOccurrenceVersion = Number(data.occurrenceVersion);
  const hasEventOccurrence = Boolean(eventOccurrenceId) || Number.isInteger(eventOccurrenceVersion);
  if (hasEventOccurrence) {
    if (
      String(data.reportId || '').trim() !== reportId ||
      !occurrenceId ||
      eventOccurrenceId !== occurrenceId ||
      !Number.isInteger(occurrenceVersion) ||
      eventOccurrenceVersion !== occurrenceVersion
    ) {
      return false;
    }
  } else if (occurrenceId) {
    return false;
  }

  if (data.status === 'read' || Boolean(data.dismissedAt)) {
    return true;
  }
  if (data.status !== 'unread') {
    return false;
  }

  await markNotificationAsRead(notificationId);
  return true;
};

const markCommunityOutcomeNotificationAsRead = async (
  params:
    | MarkCommunityOutcomeNotificationAsReadParams
    | MarkCommunityModerationOutcomeNotificationAsReadParams
): Promise<boolean> => {
  const notificationId = String(params.notificationId || '').trim();
  const currentUserId = String(auth.currentUser?.uid || '').trim();
  if (!currentUserId || !isSafeDocumentId(notificationId)) {
    return false;
  }

  const snapshot = await getDoc(doc(firestore, 'notifications', notificationId));
  if (!snapshot.exists()) return false;

  const data = snapshot.data() || {};
  if (
    String(data.recipientUserId || '').trim() !== currentUserId ||
    data.recipientRole !== 'user'
  ) {
    return false;
  }

  if ('resolutionStatus' in params) {
    const reportId = String(data.reportId || '').trim();
    const occurrenceId = String(data.occurrenceId || '').trim();
    const occurrenceVersion = Number(data.occurrenceVersion);
    if (
      data.type !== 'community_report_resolved' ||
      data.entityType !== 'communityReport' ||
      String(data.entityId || '').trim() !== reportId ||
      !isSafeDocumentId(reportId) ||
      !isSafeDocumentId(occurrenceId) ||
      !Number.isInteger(occurrenceVersion) ||
      occurrenceVersion < 1 ||
      data.resolutionStatus !== params.resolutionStatus
    ) {
      return false;
    }
  } else {
    const action = String(data.moderationAction || '').trim();
    const isPostAction = action === 'post_hidden';
    if (
      data.type !== 'community_moderation_action' ||
      !COMMUNITY_MODERATION_OUTCOME_ACTIONS.has(action) ||
      action !== params.moderationAction ||
      data.entityType !== (isPostAction ? 'communityPost' : 'communityThread') ||
      (isPostAction
        ? !String(data.postId || '').trim()
        : Boolean(String(data.postId || '').trim())) ||
      String(data.entityId || '').trim() !==
        String(isPostAction ? data.postId || '' : data.threadId || '').trim() ||
      !String(data.threadId || '').trim() ||
      !String(data.actorUserId || '').trim()
    ) {
      return false;
    }
    const expectedBefore =
      action === 'post_hidden' || action === 'thread_hidden'
        ? action === 'post_hidden'
          ? 'visible'
          : ['open', 'closed']
        : action === 'thread_restored'
          ? ['hidden']
          : action === 'thread_closed'
            ? ['open']
            : ['closed'];
    const beforeStatus = String(data.beforeStatus || '').trim();
    const afterStatus = String(data.afterStatus || '').trim();
    const expectedBeforeValues = Array.isArray(expectedBefore) ? expectedBefore : [expectedBefore];
    if (
      !expectedBeforeValues.includes(beforeStatus) ||
      afterStatus !==
        (action === 'post_hidden' || action === 'thread_hidden'
          ? 'hidden'
          : action === 'thread_restored' || action === 'thread_reopened'
            ? 'open'
            : 'closed')
    ) {
      return false;
    }
    const occurredAt = data.moderationOccurredAt;
    if (!occurredAt || typeof occurredAt.toMillis !== 'function' || occurredAt.toMillis() <= 0) {
      return false;
    }
  }

  if (data.status === 'read' || Boolean(data.dismissedAt)) return true;
  if (data.status !== 'unread') return false;

  await markNotificationAsRead(notificationId);
  return true;
};

const markSpecializationNotificationAsRead = async (
  params: MarkSpecializationNotificationAsReadParams
): Promise<boolean> => {
  const notificationId = String(params.notificationId || '').trim();
  const specializationId = String(params.specializationId || '').trim();
  const currentUserId = String(auth.currentUser?.uid || '').trim();
  if (!currentUserId || !isSafeDocumentId(notificationId) || !specializationId) {
    return false;
  }

  const notificationRef = doc(firestore, 'notifications', notificationId);
  const snapshot = await getDoc(notificationRef);
  if (!snapshot.exists()) {
    return false;
  }

  const data = snapshot.data() || {};
  if (
    String(data.recipientUserId || '').trim() !== currentUserId ||
    data.type !== 'admin_new_specialization_suggestion' ||
    data.entityType !== 'specialization' ||
    String(data.entityId || '').trim() !== specializationId
  ) {
    return false;
  }

  if (data.status === 'read' || Boolean(data.dismissedAt)) {
    return true;
  }
  if (data.status !== 'unread') {
    return false;
  }

  await markNotificationAsRead(notificationId);
  return true;
};

const markEquipmentTypeSuggestionNotificationAsRead = async (
  params: MarkEquipmentTypeSuggestionNotificationAsReadParams
): Promise<boolean> => {
  const notificationId = String(params.notificationId || '').trim();
  const suggestionId = String(params.suggestionId || '').trim();
  const currentUserId = String(auth.currentUser?.uid || '').trim();
  if (!currentUserId || !isSafeDocumentId(notificationId) || !isSafeDocumentId(suggestionId, 256)) {
    return false;
  }

  const notificationRef = doc(firestore, 'notifications', notificationId);
  const snapshot = await getDoc(notificationRef);
  if (!snapshot.exists()) {
    return false;
  }

  const data = snapshot.data() || {};
  const storedLink = String(data.deepLink || '').trim();
  let parsedLink: URL;
  try {
    parsedLink = new URL(storedLink, 'https://ratujsprzet.pl');
  } catch (_error) {
    return false;
  }

  if (
    String(data.recipientUserId || '').trim() !== currentUserId ||
    data.recipientRole !== 'admin' ||
    data.type !== 'admin_new_equipment_type_suggestion' ||
    data.entityType !== 'equipmentTypeSuggestion' ||
    String(data.entityId || '').trim() !== suggestionId ||
    parsedLink.pathname !== '/admin/equipment-types' ||
    parsedLink.searchParams.get('view') !== 'moderation' ||
    parsedLink.searchParams.get('equipmentTypeSuggestionId') !== suggestionId ||
    parsedLink.searchParams.get('notificationId') !== notificationId
  ) {
    return false;
  }

  if (Boolean(data.dismissedAt)) {
    return false;
  }
  if (data.status === 'read') {
    return true;
  }
  if (data.status !== 'unread') {
    return false;
  }

  await markNotificationAsRead(notificationId);
  return true;
};

const markEquipmentTypeSuggestionOutcomeNotificationAsRead = async (
  params: MarkEquipmentTypeSuggestionOutcomeNotificationAsReadParams
): Promise<boolean> => {
  const notificationId = String(params.notificationId || '').trim();
  const suggestionId = String(params.suggestionId || '').trim();
  const currentUserId = String(auth.currentUser?.uid || '').trim();
  const expectedType =
    params.outcome === 'accepted'
      ? 'equipment_type_suggestion_accepted'
      : params.outcome === 'rejected'
        ? 'equipment_type_suggestion_rejected'
        : null;
  if (
    !currentUserId ||
    !expectedType ||
    !isSafeDocumentId(notificationId) ||
    !isSafeDocumentId(suggestionId, 256)
  ) {
    return false;
  }

  const snapshot = await getDoc(doc(firestore, 'notifications', notificationId));
  if (!snapshot.exists()) {
    return false;
  }

  const data = snapshot.data() || {};
  const storedLink = String(data.deepLink || '').trim();
  let parsedLink: URL;
  try {
    parsedLink = new URL(storedLink, 'https://ratujsprzet.pl');
  } catch (_error) {
    return false;
  }

  if (
    String(data.recipientUserId || '').trim() !== currentUserId ||
    data.recipientRole !== 'user' ||
    data.type !== expectedType ||
    data.entityType !== 'equipmentTypeSuggestion' ||
    String(data.entityId || '').trim() !== suggestionId ||
    !storedLink.startsWith('/app/service/equipment-types') ||
    parsedLink.pathname !== '/app/service/equipment-types' ||
    parsedLink.searchParams.get('equipmentTypeSuggestionId') !== suggestionId ||
    parsedLink.searchParams.get('notificationId') !== notificationId
  ) {
    return false;
  }

  if (Boolean(data.dismissedAt)) {
    return false;
  }
  if (data.status === 'read') {
    return true;
  }
  if (data.status !== 'unread') {
    return false;
  }

  await markNotificationAsRead(notificationId);
  return true;
};

const markSpecializationOutcomeNotificationAsRead = async (
  params: MarkSpecializationOutcomeNotificationAsReadParams
): Promise<boolean> => {
  const notificationId = String(params.notificationId || '').trim();
  const specializationId = String(params.specializationId || '').trim();
  const currentUserId = String(auth.currentUser?.uid || '').trim();
  const expectedType =
    params.outcome === 'accepted'
      ? 'specialization_suggestion_accepted'
      : params.outcome === 'rejected'
        ? 'specialization_suggestion_rejected'
        : null;
  if (
    !currentUserId ||
    !expectedType ||
    !isSafeDocumentId(notificationId) ||
    !isSafeDocumentId(specializationId, 256)
  ) {
    return false;
  }

  const snapshot = await getDoc(doc(firestore, 'notifications', notificationId));
  if (!snapshot.exists()) {
    return false;
  }

  const data = snapshot.data() || {};
  if (
    String(data.recipientUserId || '').trim() !== currentUserId ||
    data.type !== expectedType ||
    data.entityType !== 'specialization' ||
    String(data.entityId || '').trim() !== specializationId
  ) {
    return false;
  }

  if (data.status === 'read' || Boolean(data.dismissedAt)) {
    return true;
  }
  if (data.status !== 'unread') {
    return false;
  }

  await markNotificationAsRead(notificationId);
  return true;
};

const markProductFeedbackNotificationAsRead = async (
  params: MarkProductFeedbackNotificationAsReadParams
): Promise<boolean> => {
  const notificationId = String(params.notificationId || '').trim();
  const reportId = String(params.reportId || '').trim();
  const currentUserId = String(auth.currentUser?.uid || '').trim();
  if (!currentUserId || !isSafeDocumentId(notificationId) || !isSafeDocumentId(reportId, 128)) {
    return false;
  }

  const notificationRef = doc(firestore, 'notifications', notificationId);
  const snapshot = await getDoc(notificationRef);
  if (!snapshot.exists()) {
    return false;
  }

  const data = snapshot.data() || {};
  if (
    String(data.recipientUserId || '').trim() !== currentUserId ||
    data.type !== 'admin_new_product_feedback_report' ||
    data.entityType !== 'productFeedbackReport' ||
    String(data.entityId || '').trim() !== reportId
  ) {
    return false;
  }

  if (data.status === 'read' || Boolean(data.dismissedAt)) {
    return true;
  }
  if (data.status !== 'unread') {
    return false;
  }

  await markNotificationAsRead(notificationId);
  return true;
};

const markProductFeedbackReporterNotificationAsRead = async (
  params: MarkProductFeedbackReporterNotificationAsReadParams
): Promise<boolean> => {
  const notificationId = String(params.notificationId || '').trim();
  const reportId = String(params.reportId || '').trim();
  const currentUserId = String(auth.currentUser?.uid || '').trim();
  if (!currentUserId || !isSafeDocumentId(notificationId) || !isSafeDocumentId(reportId, 128)) {
    return false;
  }

  const notificationRef = doc(firestore, 'notifications', notificationId);
  const snapshot = await getDoc(notificationRef);
  if (!snapshot.exists()) {
    return false;
  }

  const data = snapshot.data() || {};
  if (
    String(data.recipientUserId || '').trim() !== currentUserId ||
    data.type !== 'product_feedback_status_changed' ||
    data.entityType !== 'productFeedbackReport' ||
    String(data.entityId || '').trim() !== reportId
  ) {
    return false;
  }

  if (data.status === 'read' || Boolean(data.dismissedAt)) {
    return true;
  }
  if (data.status !== 'unread') {
    return false;
  }

  await markNotificationAsRead(notificationId);
  return true;
};

const isNotificationReadOrDismissed = async (notificationId: string): Promise<boolean> => {
  const normalizedNotificationId = String(notificationId || '').trim();
  if (!normalizedNotificationId) {
    return false;
  }

  const notificationRef = doc(firestore, 'notifications', normalizedNotificationId);
  const snapshot = await getDoc(notificationRef);
  if (!snapshot.exists()) {
    return false;
  }

  const data = snapshot.data() || {};
  return data.status === 'read' || Boolean(data.dismissedAt);
};

const dismissNotification = async (notificationId: string, userId: string): Promise<void> => {
  const normalizedNotificationId = String(notificationId || '').trim();
  const normalizedUserId = String(userId || '').trim();
  if (!normalizedNotificationId || !normalizedUserId) {
    return;
  }

  const notificationRef = doc(firestore, 'notifications', normalizedNotificationId);
  await updateDoc(notificationRef, {
    dismissedAt: serverTimestamp(),
    dismissedBy: normalizedUserId,
    updatedAt: serverTimestamp(),
  });
};

const applyBellCommand = async (command: BellCommand, currentUserId: string): Promise<void> => {
  if (command.source === 'notifications') {
    if (command.action === 'dismiss') {
      await dismissNotification(command.id, currentUserId);
      return;
    }
    await markNotificationAsRead(command.id);
    return;
  }

  if (command.action !== 'markRead') {
    throw new Error('Unsupported bell command for repair request unread.');
  }

  if (command.actor === 'service') {
    await repairRequestService.markServiceActivityAsRead(command.requestId, command.unreadSection);
    return;
  }

  await repairRequestService.markClientActivityAsRead(command.requestId, command.unreadSection);
};

const applyBellCommands = async (
  commands: BellCommand[],
  currentUserId: string
): Promise<BellCommandResult[]> => {
  const results: BellCommandResult[] = [];
  for (const command of commands) {
    try {
      await applyBellCommand(command, currentUserId);
      results.push({ command, ok: true });
    } catch (error) {
      results.push({
        command,
        ok: false,
        error: error instanceof Error ? error : new Error(String(error)),
      });
    }
  }
  return results;
};

const markEntityNotificationsAsRead = async (
  params: MarkEntityNotificationsAsReadParams
): Promise<number> => {
  const recipientUserId = String(params.recipientUserId || '').trim();
  const entityId = String(params.entityId || '').trim();
  const entityType = String(params.entityType || '').trim();

  if (!recipientUserId || !entityId || !entityType) {
    return 0;
  }

  const baseCollection = collection(firestore, 'notifications');
  let totalUpdated = 0;

  while (true) {
    const unreadEntityQuery = query(
      baseCollection,
      where('recipientUserId', '==', recipientUserId),
      where('entityType', '==', entityType),
      where('entityId', '==', entityId),
      where('status', '==', 'unread'),
      limit(ENTITY_MARK_READ_CHUNK_SIZE)
    );

    const snapshot = await getDocs(unreadEntityQuery);
    if (snapshot.empty) {
      break;
    }

    const batch = writeBatch(firestore);
    snapshot.docs.forEach((docSnapshot) => {
      batch.update(docSnapshot.ref, {
        status: 'read',
        readAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });

    await batch.commit();
    totalUpdated += snapshot.size;

    if (snapshot.size < ENTITY_MARK_READ_CHUNK_SIZE) {
      break;
    }
  }

  return totalUpdated;
};

const markRepairRequestNotificationsAsRead = async (
  params: MarkRepairRequestNotificationsAsReadParams
): Promise<number> => {
  const recipientUserId = String(params.recipientUserId || '').trim();
  const repairRequestId = String(params.repairRequestId || '').trim();

  if (!recipientUserId || !repairRequestId) {
    return 0;
  }

  let totalUpdated = 0;

  for (const entityType of REPAIR_REQUEST_ENTITY_TYPES) {
    totalUpdated += await markEntityNotificationsAsRead({
      recipientUserId,
      entityType,
      entityId: repairRequestId,
    });
  }

  const baseCollection = collection(firestore, 'notifications');
  const deepLinkPrefix = `/app/request/${repairRequestId}`;

  while (true) {
    const unreadQuery = query(
      baseCollection,
      where('recipientUserId', '==', recipientUserId),
      where('status', '==', 'unread'),
      limit(ENTITY_MARK_READ_CHUNK_SIZE)
    );

    const snapshot = await getDocs(unreadQuery);
    if (snapshot.empty) {
      break;
    }

    const matchingDocs = snapshot.docs.filter((docSnapshot) => {
      const data = docSnapshot.data() || {};
      const deepLink = String(data.deepLink || '').trim();
      if (deepLink.startsWith(deepLinkPrefix)) {
        return true;
      }

      const entityType = String(data.entityType || '').trim();
      const entityId = String(data.entityId || '').trim();
      if (entityId !== repairRequestId) {
        return false;
      }

      return REPAIR_REQUEST_ENTITY_TYPES.includes(entityType);
    });

    if (matchingDocs.length === 0) {
      break;
    }

    const batch = writeBatch(firestore);
    matchingDocs.forEach((docSnapshot) => {
      batch.update(docSnapshot.ref, {
        status: 'read',
        readAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });
    await batch.commit();
    totalUpdated += matchingDocs.length;

    if (snapshot.size < ENTITY_MARK_READ_CHUNK_SIZE) {
      break;
    }
  }

  return totalUpdated;
};

const resolveNotificationNavigationTarget = (
  notification: AppNotification,
  options: ResolveNotificationTargetOptions
): NotificationNavigationTarget => {
  const entityId = String(notification.entityId || '').trim();
  const defaultTarget = normalizeTarget(options.defaultTarget) || '/app';
  const deepLinkTarget = normalizeTarget(notification.deepLink);

  const isSuggestionType = SUGGESTION_NOTIFICATION_TYPES.has(notification.type);
  const isSpecializationDecisionType = SPECIALIZATION_DECISION_NOTIFICATION_TYPES.has(
    notification.type
  );
  const isEquipmentTypeSuggestionIntakeType = EQUIPMENT_TYPE_SUGGESTION_NOTIFICATION_TYPES.has(
    notification.type
  );
  const isEquipmentTypeSuggestionOutcomeType =
    EQUIPMENT_TYPE_SUGGESTION_OUTCOME_NOTIFICATION_TYPES.has(notification.type);
  const isEquipmentTypeSuggestionType =
    isEquipmentTypeSuggestionIntakeType || isEquipmentTypeSuggestionOutcomeType;
  const isCommunityType = COMMUNITY_NOTIFICATION_TYPES.has(notification.type);
  const isVerificationType = notification.type === 'admin_new_verification';
  const isServiceVerificationDecisionType = SERVICE_VERIFICATION_DECISION_NOTIFICATION_TYPES.has(
    notification.type
  );
  const isCommunityReportType = notification.type === 'admin_new_community_report';
  const isCommunityReportOutcomeType = notification.type === 'community_report_resolved';
  const isCommunityModerationOutcomeType =
    notification.type === 'community_moderation_action' &&
    COMMUNITY_MODERATION_OUTCOME_ACTIONS.has(String(notification.moderationAction || '').trim());
  const isProductFeedbackReportType = notification.type === 'admin_new_product_feedback_report';
  const isProductFeedbackReporterStatusType =
    notification.type === 'product_feedback_status_changed';
  const isSpecializationSuggestionType =
    notification.type === 'admin_new_specialization_suggestion';

  const computedPrimary = isEquipmentTypeSuggestionOutcomeType
    ? getEquipmentTypeSuggestionOutcomeTarget(notification, entityId)
    : isEquipmentTypeSuggestionIntakeType
      ? getEquipmentTypeSuggestionTarget(notification, entityId)
      : isSpecializationSuggestionType
        ? getSpecializationSuggestionTarget(notification, entityId)
        : isProductFeedbackReporterStatusType
          ? getProductFeedbackReporterTarget(notification, entityId)
          : isProductFeedbackReportType
            ? getProductFeedbackTarget(notification, entityId)
            : isCommunityModerationOutcomeType
              ? getCommunityModerationOutcomeTarget(notification)
              : isCommunityReportOutcomeType
                ? getCommunityReportOutcomeTarget(notification)
                : isCommunityReportType
                  ? getCommunityReportTarget(notification, entityId)
                  : isCommunityType
                    ? getCommunityTarget(notification, entityId)
                    : isServiceVerificationDecisionType
                      ? getServiceVerificationDecisionTarget(notification, entityId)
                      : isVerificationType
                        ? getVerificationTarget(notification, entityId)
                        : !entityId
                          ? defaultTarget
                          : isSuggestionType
                            ? getSuggestionTarget(notification, entityId)
                            : isSpecializationDecisionType
                              ? getSpecializationPrimaryTarget(notification, entityId)
                              : defaultTarget;

  const primaryTarget =
    isCommunityType ||
    isVerificationType ||
    isServiceVerificationDecisionType ||
    isCommunityReportType ||
    isCommunityReportOutcomeType ||
    isCommunityModerationOutcomeType ||
    isProductFeedbackReportType ||
    isProductFeedbackReporterStatusType ||
    isSpecializationSuggestionType ||
    isSpecializationDecisionType ||
    isEquipmentTypeSuggestionType ||
    isSuggestionType
      ? computedPrimary
      : deepLinkTarget || computedPrimary;
  const fallbackTarget = null;
  const usesFallback = false;

  return {
    target: usesFallback ? fallbackTarget || primaryTarget : primaryTarget,
    primaryTarget,
    fallbackTarget,
    usesFallback,
  };
};

export const notificationService = {
  subscribeToUserNotifications,
  markNotificationAsRead,
  markSuggestionNotificationAsRead,
  markCommunityNotificationAsRead,
  markVerificationNotificationAsRead,
  getOwnServiceVerificationDecisionOutcome,
  markServiceVerificationDecisionNotificationAsRead,
  markCommunityReportNotificationAsRead,
  markCommunityOutcomeNotificationAsRead,
  markSpecializationNotificationAsRead,
  markSpecializationOutcomeNotificationAsRead,
  markEquipmentTypeSuggestionNotificationAsRead,
  markEquipmentTypeSuggestionOutcomeNotificationAsRead,
  markProductFeedbackNotificationAsRead,
  markProductFeedbackReporterNotificationAsRead,
  isNotificationReadOrDismissed,
  dismissNotification,
  applyBellCommand,
  applyBellCommands,
  markEntityNotificationsAsRead,
  markRepairRequestNotificationsAsRead,
  resolveNotificationNavigationTarget,
};
